/**
 * Module registry — single source of truth for the 9 vertical "clone" modules.
 * OAB and ENEM are NOT in this registry because they have bespoke logic
 * (juridico ranking, TRI, competências). Concursos is the legacy generic module.
 *
 * The shared components in `src/components/modules/*` read a `ModuleConfig`
 * from here so each per-module page can be a 4-line wrapper.
 */
import type { ModuleType } from '@/lib/moduleContext';
import type { TargetExamModule } from '@/hooks/useTargetExams';

export type ModuleOrganizationsRpc =
  | 'get_tribunais_organizations'
  | 'get_juridicas_organizations'
  | 'get_cnu_organizations'
  | 'get_policiais_organizations'
  | 'get_bancario_organizations'
  | 'get_militares_organizations'
  | 'get_residencia_organizations'
  | 'get_colegios_organizations'
  | 'get_vestibulares_organizations';

export type ModuleTotalQuestionsRpc =
  | 'get_tribunais_total_questions'
  | 'get_juridicas_total_questions'
  | 'get_cnu_total_questions'
  | 'get_policiais_total_questions'
  | 'get_bancario_total_questions'
  | 'get_militares_total_questions'
  | 'get_residencia_total_questions'
  | 'get_colegios_total_questions';


export type ModuleSlug =
  | 'tribunais'
  | 'juridicas'
  | 'cnu'
  | 'policiais'
  | 'bancario'
  | 'militares'
  | 'residencia'
  | 'colegios'
  | 'vestibulares';

/**
 * IMPORTANT — Tailwind JIT
 * The `gradient*` strings below are used verbatim as className. They MUST
 * appear literally in this file so the Tailwind JIT picks them up while
 * scanning `src/**`. Do not interpolate the colors at runtime.
 */
export interface ModuleConfig {
  slug: ModuleSlug;
  /** Display name used in titles, sidebar header, etc. ("Tribunais"). */
  displayName: string;
  /** Optional sidebar subtitle ("Módulo Vestibulares"). */
  headerSubtitle?: string;
  /** Route prefix without trailing slash. ("/tribunais", "/carreiras-juridicas"). */
  routePrefix: string;
  /** The DB enum value (module_type column). Use `moduleType` for queries. */
  moduleType: ModuleType;
  /** Key used by `useTargetExams(slug)`. Usually equal to the route prefix segment. */
  targetExamsKey: TargetExamModule;
  /** Supabase RPC that returns the org whitelist for this module. */
  organizationsRpc: ModuleOrganizationsRpc;
  /** Supabase RPC that returns total available questions. Optional for modules backed by `examTypeId`. */
  totalQuestionsRpc?: ModuleTotalQuestionsRpc;
  /** Optional exam_type UUID — for modules backed by a single exam type. */
  examTypeId?: string;
  /** Theme gradient classes (must be literal Tailwind strings). */
  theme: {
    /** Active-state gradient for sidebar items & avatar. */
    gradientActive: string;
    /** Solid foreground accent class (text-…). */
    accentText: string;
    /** Solid background accent class with /10 alpha. */
    accentBg10: string;
    /** Solid background accent class with /30 alpha. */
    accentBg30: string;
  };
  /** Which sidebar items to render. Vestibulares hides a few. */
  sidebar: {
    showSmartReview: boolean;
    showWeakAreas: boolean;
    showErrorPatterns: boolean;
    showAchievements: boolean;
    showPomodoro: boolean;
    /** Label for the "questões" entry. Default: `Questões ${displayName}`. */
    questionsLabel?: string;
  };
}

export const MODULES: Record<ModuleSlug, ModuleConfig> = {
  tribunais: {
    slug: 'tribunais',
    displayName: 'Tribunais',
    routePrefix: '/tribunais',
    moduleType: 'tribunais',
    targetExamsKey: 'tribunais',
    organizationsRpc: 'get_tribunais_organizations',
    totalQuestionsRpc: 'get_tribunais_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-indigo-600 to-indigo-800',
      accentText: 'text-indigo-600',
      accentBg10: 'bg-indigo-500/10',
      accentBg30: 'bg-indigo-500/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  juridicas: {
    slug: 'juridicas',
    displayName: 'Jurídicas',
    routePrefix: '/carreiras-juridicas',
    moduleType: 'juridicas',
    targetExamsKey: 'carreiras-juridicas',
    organizationsRpc: 'get_juridicas_organizations',
    totalQuestionsRpc: 'get_juridicas_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-[#7C1D2E] to-[#7C1D2E]',
      accentText: 'text-[#7C1D2E]',
      accentBg10: 'bg-[#7C1D2E]/10',
      accentBg30: 'bg-[#7C1D2E]/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  cnu: {
    slug: 'cnu',
    displayName: 'CNU',
    routePrefix: '/cnu',
    moduleType: 'cnu',
    targetExamsKey: 'cnu',
    organizationsRpc: 'get_cnu_organizations',
    totalQuestionsRpc: 'get_cnu_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-[#00853F] to-[#00853F]',
      accentText: 'text-[#00853F]',
      accentBg10: 'bg-[#00853F]/10',
      accentBg30: 'bg-[#00853F]/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  policiais: {
    slug: 'policiais',
    displayName: 'Carreiras Policiais',
    routePrefix: '/carreiras-policiais',
    moduleType: 'policiais',
    targetExamsKey: 'carreiras-policiais',
    organizationsRpc: 'get_policiais_organizations',
    totalQuestionsRpc: 'get_policiais_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-blue-700 to-blue-900',
      accentText: 'text-blue-700',
      accentBg10: 'bg-blue-700/10',
      accentBg30: 'bg-blue-700/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  bancario: {
    slug: 'bancario',
    displayName: 'Bancário',
    routePrefix: '/bancario',
    moduleType: 'bancario',
    targetExamsKey: 'bancario',
    organizationsRpc: 'get_bancario_organizations',
    totalQuestionsRpc: 'get_bancario_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-[#FFD500] to-[#FFD500]',
      accentText: 'text-[#FFD500]',
      accentBg10: 'bg-[#FFD500]/10',
      accentBg30: 'bg-[#FFD500]/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  militares: {
    slug: 'militares',
    displayName: 'Militares',
    routePrefix: '/militares',
    moduleType: 'militares',
    targetExamsKey: 'militares',
    organizationsRpc: 'get_militares_organizations',
    totalQuestionsRpc: 'get_militares_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-[#4B5320] to-[#4B5320]',
      accentText: 'text-[#4B5320]',
      accentBg10: 'bg-[#4B5320]/10',
      accentBg30: 'bg-[#4B5320]/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  residencia: {
    slug: 'residencia',
    displayName: 'Residência Médica',
    routePrefix: '/residencia',
    moduleType: 'residencia',
    targetExamsKey: 'residencia',
    organizationsRpc: 'get_residencia_organizations',
    totalQuestionsRpc: 'get_residencia_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-[#0E7C5A] to-[#0E7C5A]',
      accentText: 'text-[#0E7C5A]',
      accentBg10: 'bg-[#0E7C5A]/10',
      accentBg30: 'bg-[#0E7C5A]/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  colegios: {
    slug: 'colegios',
    displayName: 'Colégios',
    routePrefix: '/colegios',
    moduleType: 'colegios',
    targetExamsKey: 'colegios',
    organizationsRpc: 'get_colegios_organizations',
    totalQuestionsRpc: 'get_colegios_total_questions',
    theme: {
      gradientActive: 'bg-gradient-to-r from-[#3D5240] to-[#3D5240]',
      accentText: 'text-[#3D5240]',
      accentBg10: 'bg-[#3D5240]/10',
      accentBg30: 'bg-[#3D5240]/30',
    },
    sidebar: { showSmartReview: true, showWeakAreas: true, showErrorPatterns: true, showAchievements: true, showPomodoro: true },
  },
  vestibulares: {
    slug: 'vestibulares',
    displayName: 'Vestibulares',
    headerSubtitle: 'Módulo Vestibulares',
    routePrefix: '/vestibulares',
    moduleType: 'vestibulares',
    targetExamsKey: 'vestibulares',
    organizationsRpc: 'get_vestibulares_organizations',
    // vestibulares uses examTypeId (no per-module total RPC)
    examTypeId: 'c1f0cb51-d55a-41c6-9509-4a8e6439ad45',
    theme: {
      gradientActive: 'bg-gradient-to-r from-indigo-600 to-indigo-700',
      accentText: 'text-indigo-600',
      accentBg10: 'bg-indigo-500/10',
      accentBg30: 'bg-indigo-500/30',
    },
    sidebar: {
      showSmartReview: false,
      showWeakAreas: false,
      showErrorPatterns: false,
      showAchievements: false,
      showPomodoro: true,
    },
  },
};

export const MODULE_SLUGS = Object.keys(MODULES) as ModuleSlug[];
